import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  Search,
  RefreshCcw,
  Printer,
  Calendar,
  MapPin,
  User,
  Hash,
} from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

function s(v) {
  return (v ?? "").toString().trim();
}
function toInt(v, def = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}

// dd/MM/yyyy -> Date (local)
function parseDDMMYYYY(ddmmyyyy) {
  const t = s(ddmmyyyy);
  if (!t) return null;
  const parts = t.split("/");
  if (parts.length !== 3) return null;
  const dd = toInt(parts[0], 0);
  const mm = toInt(parts[1], 0);
  const yyyy = toInt(parts[2], 0);
  if (!dd || !mm || !yyyy) return null;
  const d = new Date(yyyy, mm - 1, dd);
  // validate exact
  if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
  return d;
}

// Date -> dd/MM/yyyy
function formatDDMMYYYY(date) {
  if (!(date instanceof Date)) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = String(date.getFullYear());
  return `${dd}/${mm}/${yyyy}`;
}

// simple debounce hook
function useDebounce(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const monthOptions = [
  { label: "Select", value: "Select" },
  { label: "January", value: "January" },
  { label: "February", value: "February" },
  { label: "March", value: "March" },
  { label: "April", value: "April" },
  { label: "May", value: "May" },
  { label: "June", value: "June" },
  { label: "July", value: "July" },
  { label: "August", value: "August" },
  { label: "September", value: "September" },
  { label: "October", value: "October" },
  { label: "November", value: "November" },
  { label: "December", value: "December" },
];

function buildYearOptions() {
  // ASP list ends 2020, but React should support current year too.
  // We'll generate 2000..(currentYear+1)
  const nowY = new Date().getFullYear();
  const years = [];
  years.push({ label: "Select", value: "Select" });
  for (let y = 2000; y <= nowY + 1; y++) {
    years.push({ label: String(y), value: String(y) });
  }
  return years;
}

const MonthlyWorkingDayReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branchType = localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "HO";

  // filters
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("Select");

  const [monthName, setMonthName] = useState("Select");
  const [year, setYear] = useState("Select");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");

  // autocomplete
  const [codeSug, setCodeSug] = useState([]);
  const [nameSug, setNameSug] = useState([]);
  const [showCodeSug, setShowCodeSug] = useState(false);
  const [showNameSug, setShowNameSug] = useState(false);

  const debEmpCode = useDebounce(empCode, 300);
  const debEmpName = useDebounce(empName, 300);

  // table
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-35", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  // ✅ same rules as ASP Check_from_to()
const validateDates = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const f = fromDate ? new Date(fromDate) : null; // expects yyyy-MM-dd
  const t = toDate ? new Date(toDate) : null;

  if (f && isNaN(f.getTime())) {
    toast.warn("Invalid From Date");
    return false;
  }
  if (t && isNaN(t.getTime())) {
    toast.warn("Invalid To Date");
    return false;
  }

  if (f) f.setHours(0, 0, 0, 0);
  if (t) t.setHours(0, 0, 0, 0);

  if (f && f > today) {
    toast.warn("Select from date lesser than current date");
    return false;
  }
  if (t && t > today) {
    toast.warn("Select to date lesser than or Equal to Current date");
    return false;
  }
  if (f && t && t < f) {
    toast.warn("Please select from date less than to date");
    return false;
  }
  return true;
};


  const fetchMeta = async () => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!s(loginBranchId)) {
      toast.warn("Missing login branchId in localStorage.");
      return;
    }
    if (!s(branchType)) {
      toast.warn("Missing branchType in localStorage.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/monthly-working-day/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId),
          branchType: String(branchType),
        },
      });

      if (res?.data?.success) {
        const list = res.data.branches || [];
        setSites(list);

        // default site = login branch (like ASP)
        const def = res.data.defaultBranchId ? String(res.data.defaultBranchId) : "Select";
        setSiteId(def);
      } else {
        toast.error(res?.data?.message || "Failed to load sites.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta load failed");
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (p = page) => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!s(loginBranchId)) {
      toast.warn("Missing login branchId.");
      return;
    }
    if (!s(branchType)) {
      toast.warn("Missing branchType.");
      return;
    }
    if (monthName === "Select") {
      toast.warn("Please Select Month");
      return;
    }
    if (year === "Select") {
      toast.warn("Please Select Year");
      return;
    }
    if (!validateDates()) return;

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/monthly-working-day/data`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId),
          branchType: String(branchType),
          siteId: siteId, // can be Select
          monthName,
          year,
          fromDate: s(fromDate),
          toDate: s(toDate),
          empCode: s(empCode),
          empName: s(empName),
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed to load report.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  // default Month/Year like ASP
  useEffect(() => {
    const now = new Date();
    const monthLabel = monthOptions[now.getMonth() + 1]?.label || "Select";
    setMonthName(monthLabel);
    setYear(String(now.getFullYear()));
    setFromDate(""); // ASP keeps empty by default
    setToDate("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // load meta on mount
  useEffect(() => {
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // auto-load data when meta + defaults ready
  useEffect(() => {
    // fetch data only if we have site resolved (or at least tried)
    if (sites.length >= 0 && monthName !== "Select" && year !== "Select") {
      setPage(1);
      fetchData(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, monthName, year]);

  // pagination fetch
  useEffect(() => {
    fetchData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // autocomplete: employee code
  useEffect(() => {
    const run = async () => {
      try {
        if (!showCodeSug) return;
        if (!s(finYearId) || !s(branchType)) return;
        const sid = siteId && siteId !== "Select" ? siteId : loginBranchId;
        if (!sid) return;

        if (!s(debEmpCode) || debEmpCode.length < 1) {
          setCodeSug([]);
          return;
        }

        const res = await axios.get(`${BASE_URL}/api/monthly-working-day/autocomplete/code`, {
          params: {
            finYearId: Number(finYearId),
            siteId: Number(sid),
            branchType: String(branchType),
            q: debEmpCode,
          },
        });

        if (res?.data?.success) setCodeSug(res.data.items || []);
      } catch {
        // silent
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debEmpCode, showCodeSug, siteId]);

  // autocomplete: employee name
  useEffect(() => {
    const run = async () => {
      try {
        if (!showNameSug) return;
        if (!s(finYearId) || !s(branchType)) return;
        const sid = siteId && siteId !== "Select" ? siteId : loginBranchId;
        if (!sid) return;

        if (!s(debEmpName) || debEmpName.length < 1) {
          setNameSug([]);
          return;
        }

        const res = await axios.get(`${BASE_URL}/api/monthly-working-day/autocomplete/name`, {
          params: {
            finYearId: Number(finYearId),
            siteId: Number(sid),
            branchType: String(branchType),
            q: debEmpName,
          },
        });

        if (res?.data?.success) setNameSug(res.data.items || []);
      } catch {
        // silent
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debEmpName, showNameSug, siteId]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchData(1);
  };

  const handleRefresh = () => fetchData(page);

  const handlePrint = () => {
    if (monthName === "Select") return toast.warn("Please Select Month");
    if (year === "Select") return toast.warn("Please Select Year");
    if (!validateDates()) return;

    const params = new URLSearchParams({
      finYearId: String(finYearId),
      branchId: String(loginBranchId),
      branchType: String(branchType),
      siteId: String(siteId),
      monthName: String(monthName),
      year: String(year),
      fromDate: s(fromDate),
      toDate: s(toDate),
      empCode: s(empCode),
      empName: s(empName),
    });

    // open print page (React route)
    window.open(`/PayRoll/MonthlyWorkingDayReport_Print?${params.toString()}`, "_blank");
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);
  const yearOptions = useMemo(() => buildYearOptions(), []);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">MONTHLY WORKING DAY REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Header row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Monthly Working Day Report
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Filter / Search / Pagination / Print
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleRefresh} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handlePrint} className={primaryBtn} disabled={loading}>
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {/* Employee Code */}
              <div className="relative">
                <label className={labelBase}>Employee Code</label>
                <div className="mt-2 flex items-center gap-2">
                  <Hash className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    value={empCode}
                    onChange={(e) => setEmpCode(e.target.value)}
                    onFocus={() => setShowCodeSug(true)}
                    onBlur={() => setTimeout(() => setShowCodeSug(false), 150)}
                    placeholder="Type employee code..."
                    className={inputBase}
                  />
                </div>

                {showCodeSug && codeSug.length > 0 && (
                  <div className={`absolute z-20 mt-2 w-full rounded-2xl border shadow-xl overflow-hidden ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                    <div className="max-h-56 overflow-auto">
                      {codeSug.slice(0, 10).map((item, idx) => (
                        <button
                          type="button"
                          key={`${item}-${idx}`}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            isDark ? "hover:bg-white/10 text-gray-200" : "hover:bg-gray-50 text-gray-800"
                          }`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setEmpCode(item);
                            setShowCodeSug(false);
                          }}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Employee Name */}
              <div className="relative">
                <label className={labelBase}>Employee Name</label>
                <div className="mt-2 flex items-center gap-2">
                  <User className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    value={empName}
                    onChange={(e) => setEmpName(e.target.value)}
                    onFocus={() => setShowNameSug(true)}
                    onBlur={() => setTimeout(() => setShowNameSug(false), 150)}
                    placeholder="Type employee name..."
                    className={inputBase}
                  />
                </div>

                {showNameSug && nameSug.length > 0 && (
                  <div className={`absolute z-20 mt-2 w-full rounded-2xl border shadow-xl overflow-hidden ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                    <div className="max-h-56 overflow-auto">
                      {nameSug.slice(0, 10).map((item, idx) => (
                        <button
                          type="button"
                          key={`${item}-${idx}`}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            isDark ? "hover:bg-white/10 text-gray-200" : "hover:bg-gray-50 text-gray-800"
                          }`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setEmpName(item);
                            setShowNameSug(false);
                          }}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Site */}
              <div>
                <label className={labelBase}>
                  Site <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <MapPin className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select
                    value={siteId}
                    onChange={(e) => setSiteId(e.target.value)}
                    className={inputBase}
                  >
                    <option value="Select">Select</option>
                    {sites.map((b) => (
                      <option key={b.fld_branchid} value={String(b.fld_branchid)}>
                        {b.fld_branchname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

             {/* From Date */}
<div>
  <label className={labelBase}>From Date</label>
  <div className="mt-2 flex items-center gap-2">
    <Calendar className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
    <input
      type="date"
      value={fromDate} // now should be "yyyy-MM-dd"
      onChange={(e) => setFromDate(e.target.value)}
      className={inputBase}
    />
  </div>
</div>

{/* To Date */}
<div>
  <label className={labelBase}>To Date</label>
  <div className="mt-2 flex items-center gap-2">
    <Calendar className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
    <input
      type="date"
      value={toDate} // now should be "yyyy-MM-dd"
      onChange={(e) => setToDate(e.target.value)}
      className={inputBase}
    />
  </div>
</div>


              {/* Month */}
              <div>
                <label className={labelBase}>
                  Month <span className="text-red-500">*</span>
                </label>
                <select value={monthName} onChange={(e) => setMonthName(e.target.value)} className={`mt-2 ${inputBase}`}>
                  {monthOptions.map((m) => (
                    <option key={m.value} value={m.label}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Year */}
              <div>
                <label className={labelBase}>
                  Year <span className="text-red-500">*</span>
                </label>
                <select value={year} onChange={(e) => setYear(e.target.value)} className={`mt-2 ${inputBase}`}>
                  {yearOptions.map((y) => (
                    <option key={y.value} value={y.value}>
                      {y.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3 mt-6">
              <button type="submit" className={primaryBtn} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Designation</th>
                    <th className="px-4 py-4 text-center font-bold">Total Working Days</th>
                    <th className="px-4 py-4 text-center font-bold">Days Worked</th>
                    <th className="px-4 py-4 text-center font-bold">Leave Taken</th>
                    <th className="px-4 py-4 text-center font-bold">Holidays</th>
                    <th className="px-4 py-4 text-center font-bold">Absence</th>
                    <th className="px-4 py-4 text-center font-bold">LOP</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 10 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={10} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={`${r.fld_employeecode}-${idx}`}
                        className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                      >
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employeecode}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employee}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_designationname}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.Workingdays}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.present}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.leave}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.holidays}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.absentees}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.lop}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === i + 1
                        ? `${primaryBtnClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default MonthlyWorkingDayReport;
